import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Circle, Play, FileText, Headphones, Image as ImageIcon, Award, ChevronLeft, ChevronRight, RotateCcw, Send, Loader, XCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoursePlayerPage() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [currentContent, setCurrentContent] = useState(null);
  const [currentQuiz, setCurrentQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult, setQuizResult] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0); // Track current question
  const [quizTimeRemaining, setQuizTimeRemaining] = useState(null); // Quiz timer
  const [quizStartTime, setQuizStartTime] = useState(null);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [hasCertificate, setHasCertificate] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Flashcard state
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  
  // Matching game state
  const [matchingSelectedTerm, setMatchingSelectedTerm] = useState(null);
  const [matchingSelectedDef, setMatchingSelectedDef] = useState(null);
  const [matchingMatches, setMatchingMatches] = useState([]);
  const [matchingGameComplete, setMatchingGameComplete] = useState(false);
  const [shuffledTerms, setShuffledTerms] = useState([]);
  const [shuffledDefinitions, setShuffledDefinitions] = useState([]);
  
  // Memory game state
  const [memoryCards, setMemoryCards] = useState([]);
  const [memoryFlipped, setMemoryFlipped] = useState([]);
  const [memoryMatched, setMemoryMatched] = useState([]);
  const [memoryMoves, setMemoryMoves] = useState(0);
  const [memoryTimer, setMemoryTimer] = useState(0);
  const [memoryGameState, setMemoryGameState] = useState('ready'); // ready, playing, won
  const [memoryInterval, setMemoryInterval] = useState(null);
  
  // Fill-in state
  const [fillInAnswers, setFillInAnswers] = useState({});
  const [fillInGrading, setFillInGrading] = useState(false);
  const [fillInResults, setFillInResults] = useState(null);
  
  // Jeopardy state
  const [jeopardyScore, setJeopardyScore] = useState(0);
  const [jeopardyAnswered, setJeopardyAnswered] = useState([]);
  const [jeopardySelectedQ, setJeopardySelectedQ] = useState(null);
  const [jeopardyGameState, setJeopardyGameState] = useState('board'); // board, question, results
  const [jeopardyAnswer, setJeopardyAnswer] = useState('');
  const [jeopardyTimer, setJeopardyTimer] = useState(20);
  const [jeopardyInterval, setJeopardyInterval] = useState(null);
  const [jeopardyGrading, setJeopardyGrading] = useState(false);
  const [jeopardyResult, setJeopardyResult] = useState(null);

  useEffect(() => {
    loadCourse();
  }, [id]);

  // Quiz Timer Countdown
  useEffect(() => {
    if (currentQuiz && quizTimeRemaining !== null && quizTimeRemaining > 0 && !quizResult) {
      const interval = setInterval(() => {
        setQuizTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            // Auto-submit when time runs out
            submitQuiz();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [currentQuiz, quizTimeRemaining, quizResult]);

  const loadCourse = async () => {
    try {
      // Check if we're viewing a lesson or course based on the URL
      const isLesson = window.location.pathname.includes('/lesson-player/');
      const endpoint = isLesson ? `${API}/lessons/${id}/content` : `${API}/courses/${id}/content`;
      
      const response = await axios.get(endpoint, {
        withCredentials: true
      });
      
      // Load quizzes for each chapter
      const courseData = response.data;
      if (courseData.chapters) {
        await Promise.all(
          courseData.chapters.map(async (chapter) => {
            try {
              const quizResponse = await axios.get(`${API}/chapters/${chapter.id}/quizzes`);
              chapter.quizzes = quizResponse.data;
            } catch (err) {
              chapter.quizzes = [];
            }
          })
        );
      }
      
      setCourse(courseData);
      
      // Calculate completion percentage
      let totalItems = 0;
      let completedItems = 0;
      courseData.chapters?.forEach(chapter => {
        totalItems += chapter.contents?.length || 0;
        chapter.contents?.forEach(content => {
          if (courseData.user_progress?.some(p => p.content_id === content.id && p.completed)) {
            completedItems++;
          }
        });
      });
      const percentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
      setCompletionPercentage(Math.round(percentage));
      
      // Set first content as current
      if (courseData.chapters && courseData.chapters.length > 0) {
        const firstChapter = courseData.chapters[0];
        if (firstChapter.contents && firstChapter.contents.length > 0) {
          setCurrentContent(firstChapter.contents[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load course:', error);
      toast.error(error.response?.data?.detail || 'Failed to load course content');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const requestCertificate = async () => {
    try {
      const response = await axios.post(`${API}/courses/${id}/request-certificate`, {}, {
        withCredentials: true
      });
      toast.success('🎉 Certificate issued! Check your dashboard.');
      setHasCertificate(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request certificate');
    }
  };

  const markComplete = async (chapterId, contentId) => {
    try {
      await axios.post(
        `${API}/courses/${id}/progress`,
        null,
        {
          params: { chapter_id: chapterId, content_id: contentId, completed: true },
          withCredentials: true
        }
      );
      toast.success('Marked as complete!');
      loadCourse(); // Reload to update progress
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const isContentCompleted = (contentId) => {
    if (!course?.user_progress) return false;
    return course.user_progress.some(p => p.content_id === contentId && p.completed);
  };
  
  // Initialize matching game when content changes
  const initializeMatchingGame = (data) => {
    if (!data?.pairs) return;
    const terms = [...data.pairs].sort(() => Math.random() - 0.5);
    const definitions = [...data.pairs].sort(() => Math.random() - 0.5);
    setShuffledTerms(terms);
    setShuffledDefinitions(definitions);
    setMatchingSelectedTerm(null);
    setMatchingSelectedDef(null);
    setMatchingMatches([]);
    setMatchingGameComplete(false);
  };
  
  // Initialize memory game
  const initializeMemoryGame = (data) => {
    if (!data?.cards) return;
    const cards = [];
    data.cards.forEach((card, idx) => {
      cards.push({ id: `term-${idx}`, type: 'term', content: card.term, pairId: idx });
      cards.push({ id: `def-${idx}`, type: 'definition', content: card.definition, pairId: idx });
    });
    setMemoryCards(cards.sort(() => Math.random() - 0.5));
    setMemoryFlipped([]);
    setMemoryMatched([]);
    setMemoryMoves(0);
    setMemoryTimer(0);
    setMemoryGameState('ready');
    if (memoryInterval) clearInterval(memoryInterval);
  };
  
  // Matching game handlers
  const handleMatchingTermClick = (index) => {
    if (matchingMatches.some(m => m.termIndex === index)) return;
    setMatchingSelectedTerm(index);
    if (matchingSelectedDef !== null) {
      checkMatchingMatch(index, matchingSelectedDef);
    }
  };
  
  const handleMatchingDefClick = (index) => {
    if (matchingMatches.some(m => m.definitionIndex === index)) return;
    setMatchingSelectedDef(index);
    if (matchingSelectedTerm !== null) {
      checkMatchingMatch(matchingSelectedTerm, index);
    }
  };
  
  const checkMatchingMatch = (termIdx, defIdx) => {
    const term = shuffledTerms[termIdx].term;
    const definition = shuffledDefinitions[defIdx].definition;
    
    const isMatch = currentContent.data.pairs.some(pair => 
      pair.term === term && pair.definition === definition
    );
    
    if (isMatch) {
      const newMatches = [...matchingMatches, { termIndex: termIdx, definitionIndex: defIdx }];
      setMatchingMatches(newMatches);
      toast.success('Correct match!');
      
      if (newMatches.length === currentContent.data.pairs.length) {
        setMatchingGameComplete(true);
        setTimeout(() => toast.success('🎉 Congratulations! You matched all pairs!'), 500);
      }
    } else {
      toast.error('Not a match, try again!');
    }
    
    setMatchingSelectedTerm(null);
    setMatchingSelectedDef(null);
  };
  
  const resetMatchingGame = () => {
    if (currentContent?.data) {
      initializeMatchingGame(currentContent.data);
    }
  };
  
  // Memory game handlers
  const startMemoryGame = () => {
    setMemoryGameState('playing');
    setMemoryFlipped([]);
    setMemoryMatched([]);
    setMemoryMoves(0);
    setMemoryTimer(0);
    
    const interval = setInterval(() => {
      setMemoryTimer(prev => prev + 1);
    }, 1000);
    setMemoryInterval(interval);
  };
  
  const handleMemoryCardClick = (cardId) => {
    if (memoryGameState !== 'playing') return;
    if (memoryFlipped.includes(cardId)) return;
    if (memoryMatched.includes(cardId)) return;
    if (memoryFlipped.length >= 2) return;

    const newFlipped = [...memoryFlipped, cardId];
    setMemoryFlipped(newFlipped);

    if (newFlipped.length === 2) {
      setMemoryMoves(memoryMoves + 1);
      checkMemoryMatch(newFlipped);
    }
  };
  
  const checkMemoryMatch = (flippedCards) => {
    const card1 = memoryCards.find(c => c.id === flippedCards[0]);
    const card2 = memoryCards.find(c => c.id === flippedCards[1]);

    if (card1.pairId === card2.pairId) {
      setTimeout(() => {
        const newMatched = [...memoryMatched, card1.id, card2.id];
        setMemoryMatched(newMatched);
        setMemoryFlipped([]);
        
        if (newMatched.length === memoryCards.length) {
          if (memoryInterval) clearInterval(memoryInterval);
          setMemoryGameState('won');
          toast.success('🎉 You won! All pairs matched!');
        }
      }, 500);
    } else {
      setTimeout(() => {
        setMemoryFlipped([]);
      }, 1000);
    }
  };
  
  const resetMemoryGame = () => {
    if (memoryInterval) clearInterval(memoryInterval);
    if (currentContent?.data) {
      initializeMemoryGame(currentContent.data);
    }
  };
  
  // Fill-in handlers
  const handleFillInAnswerChange = (questionIdx, value) => {
    setFillInAnswers({
      ...fillInAnswers,
      [questionIdx]: value
    });
  };
  
  const handleFillInSubmit = async () => {
    setFillInGrading(true);
    try {
      const res = await axios.post(`${API}/fill-ins/${currentContent.data.id}/grade`, {
        user_answers: fillInAnswers
      }, { withCredentials: true });
      setFillInResults(res.data);
      toast.success('Grading complete!');
    } catch (error) {
      console.error('Failed to grade answers:', error);
      toast.error('Failed to grade answers. Please try again.');
    } finally {
      setFillInGrading(false);
    }
  };
  
  const resetFillIn = () => {
    const initialAnswers = {};
    currentContent.data.questions.forEach((_, idx) => {
      initialAnswers[idx] = '';
    });
    setFillInAnswers(initialAnswers);
    setFillInResults(null);
  };
  
  // Jeopardy handlers
  const handleJeopardyQuestionClick = (questionIdx) => {
    if (jeopardyAnswered.includes(questionIdx)) return;
    
    setJeopardySelectedQ(questionIdx);
    setJeopardyGameState('question');
    setJeopardyAnswer('');
    setJeopardyResult(null);
    setJeopardyTimer(20);
    
    const interval = setInterval(() => {
      setJeopardyTimer(prev => prev - 1);
    }, 1000);
    setJeopardyInterval(interval);
  };
  
  useEffect(() => {
    if (jeopardyTimer === 0 && jeopardyGameState === 'question') {
      if (jeopardyInterval) clearInterval(jeopardyInterval);
      setJeopardyResult({
        is_correct: false,
        points_awarded: 0,
        feedback: 'Time\'s up!'
      });
    }
  }, [jeopardyTimer, jeopardyGameState]);
  
  const handleJeopardySubmit = async () => {
    if (!jeopardyAnswer.trim()) return;
    if (jeopardyInterval) clearInterval(jeopardyInterval);
    
    setJeopardyGrading(true);
    try {
      const res = await axios.post(`${API}/jeopardy-games/${currentContent.data.id}/grade-answer`, {
        question_index: jeopardySelectedQ,
        user_answer: jeopardyAnswer
      }, { withCredentials: true });
      
      setJeopardyResult(res.data);
      setJeopardyScore(jeopardyScore + (res.data.points_awarded || 0));
      setJeopardyAnswered([...jeopardyAnswered, jeopardySelectedQ]);
    } catch (error) {
      console.error('Failed to grade answer:', error);
      toast.error('Failed to grade answer.');
    } finally {
      setJeopardyGrading(false);
    }
  };
  
  const handleJeopardyNext = () => {
    setJeopardyGameState('board');
    setJeopardySelectedQ(null);
    setJeopardyResult(null);
  };
  
  const resetJeopardyGame = () => {
    setJeopardyGameState('board');
    setJeopardySelectedQ(null);
    setJeopardyAnswered([]);
    setJeopardyAnswer('');
    setJeopardyScore(0);
    setJeopardyTimer(20);
    setJeopardyResult(null);
    if (jeopardyInterval) clearInterval(jeopardyInterval);
  };
  
  // Reset game states when content changes
  useEffect(() => {
    if (currentContent) {
      setCurrentCardIndex(0);
      setIsFlipped(false);
      
      if (currentContent.type === 'matching_game') {
        initializeMatchingGame(currentContent.data);
      }
      if (currentContent.type === 'memory_game') {
        initializeMemoryGame(currentContent.data);
      }
      if (currentContent.type === 'fill_in') {
        const initialAnswers = {};
        currentContent.data.questions?.forEach((_, idx) => {
          initialAnswers[idx] = '';
        });
        setFillInAnswers(initialAnswers);
        setFillInResults(null);
      }
      if (currentContent.type === 'jeopardy_game') {
        resetJeopardyGame();
      }
    }
  }, [currentContent]);

  const loadQuiz = async (quizId) => {
    try {
      const response = await axios.get(`${API}/quizzes/${quizId}`, {
        withCredentials: true
      });
      setCurrentQuiz(response.data);
      setCurrentContent(null);
      setQuizAnswers({});
      setQuizResult(null);
      // Initialize quiz timer
      const timeLimitSeconds = (response.data.time_limit_minutes || 10) * 60;
      setQuizTimeRemaining(timeLimitSeconds);
      setQuizStartTime(Date.now());
    } catch (error) {
      toast.error('Failed to load quiz');
    }
  };

  const handleQuizAnswer = (questionId, answerIndex) => {
    setQuizAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const submitQuiz = async () => {
    if (!currentQuiz) return;
    
    // Check if all questions are answered (using index as key)
    const allAnswered = currentQuiz.questions.every((q, index) => quizAnswers[String(index)] !== undefined);
    if (!allAnswered) {
      const unanswered = currentQuiz.questions.filter((q, index) => quizAnswers[String(index)] === undefined).length;
      toast.error(`Please answer all questions (${unanswered} remaining)`);
      return;
    }

    try {
      const response = await axios.post(
        `${API}/quizzes/${currentQuiz.id}/submit`,
        quizAnswers,
        { withCredentials: true }
      );
      setQuizResult(response.data);
      toast.success(response.data.passed ? 'Test passed! 🎉' : 'Test submitted. Try again if needed.');
      // Don't reload course immediately - let user see results first
    } catch (error) {
      console.error('Quiz submission error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to submit quiz';
      toast.error(`Submission failed: ${errorMsg}`);
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'video':
        return <Play className="w-5 h-5" />;
      case 'audio':
        return <Headphones className="w-5 h-5" />;
      case 'slides':
        return <ImageIcon className="w-5 h-5" />;
      case 'study_guide':
        return <FileText className="w-5 h-5" />;
      case 'flashcards':
        return <span className="text-sm">🎴</span>;
      case 'matching_game':
        return <span className="text-sm">🎯</span>;
      case 'memory_game':
        return <span className="text-sm">🃏</span>;
      case 'fill_in':
        return <span className="text-sm">✍️</span>;
      case 'jeopardy_game':
        return <span className="text-sm">🎲</span>;
      case 'quiz':
        return <Award className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-[#D4A574] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="cassette-card p-12">
            <p className="text-[#3D2817] text-xl">Not enrolled in this course</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="py-8 min-h-screen" data-testid="course-player-page">
        <div className="max-w-screen-2xl mx-auto px-4">
          
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-4xl retro-title">{course.title}</h1>
            
            {/* Progress and Certificate */}
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-[#6B4423]">Progress</p>
                <p className="text-2xl font-bold text-[#E8934D]">{completionPercentage}%</p>
              </div>
              
              {completionPercentage === 100 && !hasCertificate && (
                <button
                  onClick={requestCertificate}
                  className="retro-button flex items-center gap-2"
                >
                  <Award className="w-5 h-5" />
                  Get Certificate
                </button>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-5 gap-6">
            
            {/* Sidebar - Course Contents */}
            <div className="lg:col-span-1">
              <div className="cassette-card p-4 max-h-[80vh] overflow-y-auto sticky top-4">
                <h2 className="text-xl retro-title text-[#2C1810] mb-4">Curriculum</h2>
                
                <div className="space-y-2">
                  {course.chapters?.map((chapter, chIndex) => (
                    <div key={chapter.id}>
                      <div className="text-sm font-bold text-[#6B4423] mb-2 mt-4">
                        {chIndex + 1}. {chapter.title}
                      </div>
                      {chapter.contents?.map((content) => (
                        <button
                          key={content.id}
                          onClick={() => {
                            setCurrentContent(content);
                            setCurrentQuiz(null);
                            setQuizResult(null);
                          }}
                          className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-2 ${
                            currentContent?.id === content.id && !currentQuiz
                              ? 'bg-[#E8934D] text-white'
                              : 'hover:bg-[#F4E4C1] text-[#3D2817]'
                          }`}
                          data-testid={`content-${content.id}`}
                        >
                          {isContentCompleted(content.id) ? (
                            <CheckCircle className="w-4 h-4 text-[#4D9E7C] flex-shrink-0" />
                          ) : (
                            <Circle className="w-4 h-4 flex-shrink-0" />
                          )}
                          <span className="flex-1 text-sm">{content.title}</span>
                          {getContentIcon(content.type)}
                        </button>
                      ))}
                      
                      {/* Chapter Quizzes */}
                      {chapter.quizzes?.map((quiz) => (
                        <button
                          key={quiz.id}
                          onClick={() => loadQuiz(quiz.id)}
                          className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-2 ${
                            currentQuiz?.id === quiz.id
                              ? 'bg-[#E8934D] text-white'
                              : 'hover:bg-[#F4E4C1] text-[#3D2817]'
                          }`}
                          data-testid={`quiz-${quiz.id}`}
                        >
                          <Award className="w-4 h-4 flex-shrink-0" />
                          <span className="flex-1 text-sm font-bold">{quiz.title}</span>
                          {quiz.attempts && quiz.attempts.length > 0 && quiz.attempts[0].passed && (
                            <CheckCircle className="w-4 h-4 text-[#4D9E7C]" />
                          )}
                        </button>
                      ))}
                    </div>
                  ))}
                  
                  {/* Final Exam */}
                  {course.final_exam && (
                    <div className="mt-6">
                      <div className="text-sm font-bold text-[#6B4423] mb-2">
                        🎓 Final Exam
                      </div>
                      <button
                        onClick={() => loadQuiz(course.final_exam.id)}
                        className={`w-full text-left p-4 rounded-lg transition-colors flex items-center gap-3 border-2 ${
                          currentQuiz?.id === course.final_exam.id
                            ? 'bg-[#E8934D] text-white border-[#E8934D]'
                            : 'bg-gradient-to-r from-yellow-50 to-orange-50 hover:from-yellow-100 hover:to-orange-100 text-[#3D2817] border-orange-300'
                        }`}
                        data-testid={`final-exam-${course.final_exam.id}`}
                      >
                        <Award className="w-6 h-6 flex-shrink-0 text-orange-600" />
                        <div className="flex-1">
                          <div className="font-bold">{course.final_exam.title}</div>
                          <div className="text-xs opacity-75">{course.final_exam.questions?.length || 0} Questions</div>
                        </div>
                        {course.final_exam.attempts && course.final_exam.attempts.length > 0 && course.final_exam.attempts[0].passed && (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="lg:col-span-4">
              {currentContent ? (
                <div className="cassette-card p-8">
                  {/* Header - Title and Desktop Mark Complete Button */}
                  <div className="flex flex-col md:flex-row justify-between md:items-start mb-6 gap-4">
                    <h2 className="text-3xl retro-title">{currentContent.title}</h2>
                    {!isContentCompleted(currentContent.id) && (
                      <button
                        onClick={() => {
                          const chapter = course.chapters.find(ch => 
                            ch.contents.some(c => c.id === currentContent.id)
                          );
                          if (chapter) {
                            markComplete(chapter.id, currentContent.id);
                          }
                        }}
                        className="retro-button-small hidden md:block"
                      >
                        Mark Complete
                      </button>
                    )}
                  </div>

                  {/* Render content based on type */}
                  {currentContent.type === 'video' && currentContent.content_url && (
                    <div className="video-card mb-6">
                      <video controls className="w-full" data-testid="video-player">
                        <source src={currentContent.content_url.startsWith('http') ? currentContent.content_url : `${BACKEND_URL}${currentContent.content_url}`} />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  )}

                  {currentContent.type === 'audio' && currentContent.content_url && (
                    <div className="cassette-card p-6 mb-6">
                      <audio controls className="w-full" data-testid="audio-player">
                        <source src={currentContent.content_url.startsWith('http') ? currentContent.content_url : `${BACKEND_URL}${currentContent.content_url}`} />
                        Your browser does not support the audio tag.
                      </audio>
                    </div>
                  )}

                  {currentContent.type === 'text' && currentContent.content_text && (
                    <div className="cassette-card p-6 prose max-w-none">
                      <div className="text-[#3D2817] whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: currentContent.content_text }} />
                    </div>
                  )}

                  {currentContent.type === 'slides' && currentContent.content_url && (
                    <div className="cassette-card p-6">
                      <img
                        src={currentContent.content_url.startsWith('http') ? currentContent.content_url : `${BACKEND_URL}${currentContent.content_url}`}
                        alt={currentContent.title}
                        className="w-full"
                        data-testid="slides-image"
                      />
                    </div>
                  )}

                  {/* Auto-Generated Course Materials */}
                  {currentContent.type === 'study_guide' && currentContent.data && (
                    <div className="space-y-6">
                      {currentContent.data.sections?.map((section, idx) => (
                        <div key={idx} className="cassette-card p-6">
                          <h3 className="text-2xl font-bold text-[#2C1810] mb-4">{section.heading}</h3>
                          <div className="prose max-w-none text-[#3D2817]" dangerouslySetInnerHTML={{ __html: section.content }} />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Flashcards - Inline Viewer */}
                  {currentContent.type === 'flashcards' && currentContent.data?.cards && (
                    <div className="space-y-6">
                      <div className="text-center mb-4">
                        <span className="text-[#6B4423] font-bold">
                          Card {currentCardIndex + 1} of {currentContent.data.cards.length}
                        </span>
                      </div>
                      
                      <div 
                        onClick={() => setIsFlipped(!isFlipped)}
                        className="cassette-card p-12 min-h-[300px] flex items-center justify-center cursor-pointer hover:shadow-xl transition-shadow"
                      >
                        <div className="text-center">
                          <p className="text-sm text-[#6B4423] mb-4">
                            {isFlipped ? 'Answer' : 'Question'}
                          </p>
                          <p className="text-2xl text-[#2C1810] font-bold">
                            {isFlipped 
                              ? currentContent.data.cards[currentCardIndex]?.answer 
                              : currentContent.data.cards[currentCardIndex]?.question
                            }
                          </p>
                          <p className="text-sm text-[#6B4423] mt-6">Click to flip</p>
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                        <button
                          onClick={() => {
                            setCurrentCardIndex(Math.max(0, currentCardIndex - 1));
                            setIsFlipped(false);
                          }}
                          disabled={currentCardIndex === 0}
                          className="retro-button-small flex items-center gap-2 w-full sm:w-auto justify-center"
                        >
                          <ChevronLeft className="w-4 h-4" /> Previous
                        </button>
                        <button
                          onClick={() => {
                            setCurrentCardIndex(Math.min(currentContent.data.cards.length - 1, currentCardIndex + 1));
                            setIsFlipped(false);
                          }}
                          disabled={currentCardIndex === currentContent.data.cards.length - 1}
                          className="retro-button-small flex items-center gap-2 w-full sm:w-auto justify-center"
                        >
                          Next <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Matching Game - INTERACTIVE */}
                  {currentContent.type === 'matching_game' && currentContent.data?.pairs && (
                    <div className="space-y-6">
                      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-4">
                        <h3 className="text-2xl font-bold text-[#2C1810]">Match the Pairs</h3>
                        <button onClick={resetMatchingGame} className="retro-button-small flex items-center gap-2 w-full sm:w-auto justify-center">
                          <RotateCcw className="w-4 h-4" /> Reset
                        </button>
                      </div>
                      
                      {matchingGameComplete ? (
                        <div className="cassette-card p-8 text-center">
                          <h3 className="text-3xl font-bold text-[#4D9E7C] mb-4">🎉 Perfect!</h3>
                          <p className="text-[#6B4423] text-lg">You matched all {currentContent.data.pairs.length} pairs!</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Terms Column */}
                          <div className="space-y-3">
                            <h4 className="text-lg font-bold text-[#2C1810] mb-3">Terms</h4>
                            {shuffledTerms.map((pair, idx) => {
                              const isMatched = matchingMatches.some(m => m.termIndex === idx);
                              const isSelected = matchingSelectedTerm === idx;
                              return (
                                <button
                                  key={idx}
                                  onClick={() => handleMatchingTermClick(idx)}
                                  disabled={isMatched}
                                  className={`matching-item-card w-full p-4 rounded-lg text-left transition-all ${
                                    isMatched ? 'bg-green-100 border-2 border-green-500 opacity-50' :
                                    isSelected ? 'bg-[#E8934D] text-white border-2 border-[#E8934D]' :
                                    'cassette-card hover:shadow-lg cursor-pointer'
                                  }`}
                                >
                                  <p className={`font-bold ${
                                    isMatched ? 'text-black' :
                                    isSelected ? 'text-white' : 
                                    'text-[#2C1810]'
                                  }`}>
                                    {pair.term}
                                  </p>
                                </button>
                              );
                            })}
                          </div>
                          
                          {/* Definitions Column */}
                          <div className="space-y-3">
                            <h4 className="text-lg font-bold text-[#2C1810] mb-3">Definitions</h4>
                            {shuffledDefinitions.map((pair, idx) => {
                              const isMatched = matchingMatches.some(m => m.definitionIndex === idx);
                              const isSelected = matchingSelectedDef === idx;
                              return (
                                <button
                                  key={idx}
                                  onClick={() => handleMatchingDefClick(idx)}
                                  disabled={isMatched}
                                  className={`matching-item-card w-full p-4 rounded-lg text-left transition-all ${
                                    isMatched ? 'bg-green-100 border-2 border-green-500 opacity-50' :
                                    isSelected ? 'bg-[#4D9E7C] text-white border-2 border-[#4D9E7C]' :
                                    'cassette-card hover:shadow-lg cursor-pointer'
                                  }`}
                                >
                                  <p className={`${
                                    isMatched ? 'text-black' :
                                    isSelected ? 'text-white' :
                                    'text-[#6B4423]'
                                  }`}>
                                    {pair.definition}
                                  </p>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      <div className="text-center text-[#6B4423]">
                        Matched: {matchingMatches.length} / {currentContent.data.pairs.length}
                      </div>
                    </div>
                  )}

                  {/* Memory Game - INTERACTIVE */}
                  {currentContent.type === 'memory_game' && currentContent.data?.cards && (
                    <div className="space-y-6">
                      <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                        <h3 className="text-2xl font-bold text-[#2C1810]">Memory Game</h3>
                        <div className="flex flex-wrap gap-4 items-center justify-center">
                          {memoryGameState === 'playing' && (
                            <>
                              <div className="text-[#6B4423]">
                                <Clock className="w-4 h-4 inline mr-1" />
                                {Math.floor(memoryTimer / 60)}:{(memoryTimer % 60).toString().padStart(2, '0')}
                              </div>
                              <div className="text-[#6B4423]">Moves: {memoryMoves}</div>
                            </>
                          )}
                          <button onClick={resetMemoryGame} className="retro-button-small flex items-center gap-2">
                            <RotateCcw className="w-4 h-4" /> Reset
                          </button>
                        </div>
                      </div>
                      
                      {memoryGameState === 'ready' && (
                        <div className="cassette-card p-8 text-center">
                          <p className="text-lg text-[#6B4423] mb-4">
                            Match {currentContent.data.cards.length} term-definition pairs!
                          </p>
                          <button onClick={startMemoryGame} className="retro-button">
                            Start Game
                          </button>
                        </div>
                      )}
                      
                      {memoryGameState === 'won' && (
                        <div className="cassette-card p-8 text-center">
                          <h3 className="text-3xl font-bold text-[#4D9E7C] mb-4">🎉 You Won!</h3>
                          <p className="text-[#6B4423] text-lg">
                            Time: {Math.floor(memoryTimer / 60)}:{(memoryTimer % 60).toString().padStart(2, '0')} | Moves: {memoryMoves}
                          </p>
                        </div>
                      )}
                      
                      {memoryGameState === 'playing' && (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                          {memoryCards.map((card) => {
                            const isFlipped = memoryFlipped.includes(card.id);
                            const isMatched = memoryMatched.includes(card.id);
                            
                            return (
                              <button
                                key={card.id}
                                onClick={() => handleMemoryCardClick(card.id)}
                                disabled={isFlipped || isMatched}
                                className={`aspect-square p-4 rounded-lg transition-all ${
                                  isMatched ? 'bg-green-100 border-2 border-green-500' :
                                  isFlipped ? 'bg-[#E8934D] text-white' :
                                  'cassette-card hover:shadow-lg cursor-pointer'
                                }`}
                              >
                                {(isFlipped || isMatched) ? (
                                  <div className="h-full flex flex-col items-center justify-center text-center">
                                    <p className={`text-xs mb-1 ${isMatched ? 'text-green-700' : 'text-white'}`}>
                                      {card.type === 'term' ? 'Term' : 'Definition'}
                                    </p>
                                    <p className={`text-sm font-bold ${isMatched ? 'text-green-900' : 'text-white'}`}>
                                      {card.content}
                                    </p>
                                  </div>
                                ) : (
                                  <div className="h-full flex items-center justify-center text-4xl">
                                    ?
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Fill In - INTERACTIVE with AI Grading */}
                  {currentContent.type === 'fill_in' && currentContent.data?.questions && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-2xl font-bold text-[#2C1810]">Fill in the Blanks</h3>
                        {fillInResults && (
                          <button onClick={resetFillIn} className="retro-button-small flex items-center gap-2">
                            <RotateCcw className="w-4 h-4" /> Try Again
                          </button>
                        )}
                      </div>
                      
                      <div className="space-y-4">
                        {currentContent.data.questions.map((q, idx) => {
                          const result = fillInResults?.results?.[idx];
                          
                          return (
                            <div key={idx} className="cassette-card p-6">
                              <p className="font-bold text-[#2C1810] mb-3">
                                {idx + 1}. {q.question_text?.split('[BLANK]')[0]}
                                <input
                                  type="text"
                                  value={fillInAnswers[idx] || ''}
                                  onChange={(e) => handleFillInAnswerChange(idx, e.target.value)}
                                  disabled={!!fillInResults}
                                  className="inline-block mx-2 px-3 py-1 border-2 border-[#E8934D] rounded bg-[#FFF8E7] text-[#2C1810] font-normal"
                                  placeholder="Your answer"
                                  style={{ minWidth: '150px' }}
                                />
                                {q.question_text?.split('[BLANK]')[1]}
                              </p>
                              
                              {/* Hint - Show before grading */}
                              {!fillInResults && q.context && (
                                <div className="mt-3 p-3 bg-blue-50 border-l-4 border-blue-400 rounded">
                                  <p className="text-sm text-blue-800">
                                    <strong>💡 Hint:</strong> {q.context}
                                  </p>
                                </div>
                              )}
                              
                              {/* Results - Show after grading for ALL answers */}
                              {result && (
                                <div className={`mt-4 p-4 rounded-lg ${
                                  result.evaluation === 'CORRECT' ? 'bg-green-50 border-2 border-green-500' :
                                  result.evaluation === 'CLOSE' ? 'bg-yellow-50 border-2 border-yellow-500' :
                                  'bg-red-50 border-2 border-red-500'
                                }`}>
                                  <div className="flex items-center gap-2 mb-2">
                                    {result.evaluation === 'CORRECT' && <CheckCircle className="w-5 h-5 text-green-600" />}
                                    {result.evaluation === 'CLOSE' && <CheckCircle className="w-5 h-5 text-yellow-600" />}
                                    {result.evaluation === 'INCORRECT' && <XCircle className="w-5 h-5 text-red-600" />}
                                    <span className="font-bold text-sm">
                                      {result.evaluation === 'CORRECT' ? 'Correct!' :
                                       result.evaluation === 'CLOSE' ? 'Close Enough!' :
                                       'Incorrect'}
                                    </span>
                                  </div>
                                  <p className="text-sm text-[#6B4423] mb-2">
                                    <strong>Expected Answer:</strong> {q.correct_answer}
                                  </p>
                                  {result.evaluation !== 'CORRECT' && (
                                    <p className="text-sm text-[#6B4423] mb-2">
                                      <strong>Your Answer:</strong> {fillInAnswers[idx] || '(blank)'}
                                    </p>
                                  )}
                                  <div className="mt-3 p-3 bg-white/50 rounded">
                                    <p className="text-sm text-[#2C1810] font-semibold mb-1">
                                      {result.evaluation === 'CORRECT' ? '✅ Explanation:' : 
                                       result.evaluation === 'CLOSE' ? '⚠️ Why Close Enough:' : 
                                       '❌ Explanation:'}
                                    </p>
                                    <p className="text-sm text-[#6B4423] italic">
                                      {result.feedback || result.reasoning}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {!fillInResults && (
                        <div className="text-center">
                          <button
                            onClick={handleFillInSubmit}
                            disabled={fillInGrading}
                            className="retro-button flex items-center gap-2 mx-auto"
                          >
                            {fillInGrading ? (
                              <>
                                <Loader className="w-5 h-5 animate-spin" />
                                AI is Grading...
                              </>
                            ) : (
                              <>
                                <Send className="w-5 h-5" />
                                Submit for AI Grading
                              </>
                            )}
                          </button>
                        </div>
                      )}
                      
                      {fillInResults && (
                        <div className="cassette-card p-6 text-center">
                          <h4 className="text-xl font-bold text-[#2C1810] mb-2">Results</h4>
                          <p className="text-[#6B4423]">
                            Score: {fillInResults.correct_count} / {fillInResults.total_questions}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Jeopardy Game - INTERACTIVE with Timer and AI Grading */}
                  {currentContent.type === 'jeopardy_game' && currentContent.data && (
                    <div className="space-y-6">
                      {jeopardyGameState === 'board' && (
                        <>
                          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-4">
                            <h3 className="text-2xl font-bold text-[#2C1810]">Jeopardy Game</h3>
                            <div className="flex flex-wrap gap-4 items-center justify-center">
                              <div className="text-xl font-bold text-[#4D9E7C]">
                                Score: ${jeopardyScore}
                              </div>
                              <button onClick={resetJeopardyGame} className="retro-button-small flex items-center gap-2">
                                <RotateCcw className="w-4 h-4" /> Reset
                              </button>
                            </div>
                          </div>
                          
                          {/* Jeopardy Board - Categories as columns, values top to bottom */}
                          <div className="overflow-x-auto">
                            <div className="grid grid-cols-4 gap-2 min-w-[600px]">
                              {currentContent.data.game_categories?.map((category, catIdx) => {
                                const categoryQuestions = currentContent.data.questions
                                  ?.filter(q => q.category === category)
                                  .sort((a, b) => a.value - b.value);
                                
                                return (
                                  <div key={catIdx} className="flex flex-col gap-2">
                                    {/* Category Header */}
                                    <div className="cassette-card p-3 bg-[#E8934D] text-white text-center font-bold text-sm">
                                      {category}
                                    </div>
                                    
                                    {/* Value Buttons - Top to Bottom */}
                                    {categoryQuestions?.map((q, qIdx) => {
                                      const questionIndex = currentContent.data.questions.indexOf(q);
                                      const isAnswered = jeopardyAnswered.includes(questionIndex);
                                      
                                      return (
                                        <button
                                          key={qIdx}
                                          onClick={() => handleJeopardyQuestionClick(questionIndex)}
                                          disabled={isAnswered}
                                          className={`p-4 rounded-lg font-bold text-lg transition-all ${
                                            isAnswered 
                                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                              : 'cassette-card hover:bg-[#E8934D] hover:text-white cursor-pointer'
                                          }`}
                                        >
                                          ${q.value}
                                        </button>
                                      );
                                    })}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          
                          <div className="text-center text-[#6B4423]">
                            Answered: {jeopardyAnswered.length} / {currentContent.data.questions?.length}
                          </div>
                        </>
                      )}
                      
                      {jeopardyGameState === 'question' && jeopardySelectedQ !== null && (
                        <div className="space-y-6">
                          <div className="cassette-card p-8 bg-[#E8934D] text-white">
                            <div className="flex justify-between items-center mb-4">
                              <span className="text-2xl font-bold">
                                ${currentContent.data.questions[jeopardySelectedQ].value}
                              </span>
                              <div className="flex items-center gap-2 text-2xl font-bold">
                                <Clock className="w-6 h-6" />
                                {jeopardyTimer}s
                              </div>
                            </div>
                            <h4 className="text-xl font-bold mb-4">
                              {currentContent.data.questions[jeopardySelectedQ].category}
                            </h4>
                            <p className="text-2xl text-center">
                              {currentContent.data.questions[jeopardySelectedQ].answer}
                            </p>
                          </div>
                          
                          {!jeopardyResult ? (
                            <div className="cassette-card p-6">
                              <label className="block text-[#2C1810] font-bold mb-3">
                                What is your question?
                              </label>
                              <input
                                type="text"
                                value={jeopardyAnswer}
                                onChange={(e) => setJeopardyAnswer(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleJeopardySubmit()}
                                className="w-full px-4 py-3 border-2 border-[#E8934D] rounded-lg bg-[#FFF8E7] text-[#2C1810]"
                                placeholder="What is..."
                                disabled={jeopardyGrading || jeopardyTimer === 0}
                              />
                              <div className="mt-4 text-center">
                                <button
                                  onClick={handleJeopardySubmit}
                                  disabled={jeopardyGrading || !jeopardyAnswer.trim() || jeopardyTimer === 0}
                                  className="retro-button flex items-center gap-2 mx-auto"
                                >
                                  {jeopardyGrading ? (
                                    <>
                                      <Loader className="w-5 h-5 animate-spin" />
                                      AI is Grading...
                                    </>
                                  ) : (
                                    <>
                                      <Send className="w-5 h-5" />
                                      Submit Answer
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className={`cassette-card p-8 ${
                              jeopardyResult.is_correct ? 'bg-green-50 border-2 border-green-500' : 'bg-red-50 border-2 border-red-500'
                            }`}>
                              <div className="text-center">
                                {jeopardyResult.is_correct ? (
                                  <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                                ) : (
                                  <XCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                                )}
                                <h4 className="text-2xl font-bold mb-4">
                                  {jeopardyResult.is_correct ? 'Correct!' : 'Incorrect'}
                                </h4>
                                <p className="text-xl font-bold mb-4">
                                  Points: {jeopardyResult.points_awarded > 0 ? '+' : ''}{jeopardyResult.points_awarded}
                                </p>
                                <p className="text-[#6B4423] mb-4">{jeopardyResult.feedback}</p>
                                <p className="text-[#2C1810]">
                                  <strong>Correct Answer:</strong> {currentContent.data.questions[jeopardySelectedQ].question}
                                </p>
                                <button
                                  onClick={handleJeopardyNext}
                                  className="retro-button mt-6"
                                >
                                  Next Question
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Quiz Inline Trigger */}
                  {currentContent.type === 'quiz' && currentContent.data && (
                    <div className="text-center cassette-card p-8">
                      <Award className="w-16 h-16 mx-auto text-[#E8934D] mb-4" />
                      <p className="text-lg text-[#6B4423] mb-4">
                        {currentContent.data.questions?.length || 10} question chapter test
                      </p>
                      <button
                        onClick={() => {
                          setCurrentQuiz(currentContent.data);
                          setCurrentContent(null);
                          setQuizAnswers({});
                          setQuizResult(null);
                          setCurrentQuestionIndex(0); // Reset to first question
                          // Initialize timer
                          const timeLimitSeconds = (currentContent.data.time_limit_minutes || 10) * 60;
                          setQuizTimeRemaining(timeLimitSeconds);
                          setQuizStartTime(Date.now());
                        }}
                        className="retro-button"
                      >
                        Start Chapter Test
                      </button>
                      <p className="text-sm text-[#6B4423] mt-4">Time limit: {currentContent.data.time_limit_minutes || 10} minutes</p>
                    </div>
                  )}
                </div>
              ) : currentQuiz ? (
                /* Quiz UI - Matching Testing Center Style */
                <div>
                  {!quizResult ? (
                    /* Test Taking View */
                    <div className="space-y-6">
                      {/* Sticky Timer Banner - Mobile Optimized */}
                      {quizTimeRemaining !== null && (
                        <div className={`sticky top-0 z-50 rounded-lg shadow-lg p-4 transition-colors ${
                          quizTimeRemaining < 60 
                            ? 'bg-red-600 animate-pulse' 
                            : quizTimeRemaining < 300 
                            ? 'bg-orange-500' 
                            : 'bg-[#4D9E7C]'
                        }`}>
                          <div className="flex items-center justify-between text-white">
                            <div className="flex items-center gap-2">
                              <Clock className="w-5 h-5 md:w-6 md:h-6" />
                              <span className="font-semibold text-sm md:text-base">Time Remaining</span>
                            </div>
                            <div className="text-2xl md:text-3xl font-bold font-mono">
                              {formatTime(quizTimeRemaining)}
                            </div>
                          </div>
                          {quizTimeRemaining < 60 && (
                            <p className="text-white text-xs md:text-sm mt-2 text-center font-semibold">
                              ⚠️ Less than 1 minute remaining!
                            </p>
                          )}
                        </div>
                      )}

                      {/* Header */}
                      <div className="cassette-card p-6">
                        <div className="flex items-center gap-3 mb-2">
                          <Award className="w-8 h-8 text-[#E8934D]" />
                          <h2 className="text-2xl md:text-3xl retro-title text-[#2C1810]">{currentQuiz.title}</h2>
                        </div>
                        <p className="text-[#6B4423]">
                          Passing Score: {currentQuiz.passing_score}% | {currentQuiz.questions?.length} Questions
                        </p>
                      </div>

                      {/* Current Question */}
                      {currentQuiz.questions && currentQuiz.questions[currentQuestionIndex] && (
                        <div className="cassette-card p-8">
                          <p className="text-2xl font-bold text-[#2C1810] mb-6">
                            Question {currentQuestionIndex + 1} of {currentQuiz.questions.length}
                          </p>
                          <p className="text-xl text-[#3D2817] mb-6">
                            {currentQuiz.questions[currentQuestionIndex].question_text}
                          </p>
                          
                          <div className="space-y-3">
                            {currentQuiz.questions[currentQuestionIndex].options.map((option, oIndex) => {
                              const questionKey = String(currentQuestionIndex);
                              const isSelected = quizAnswers[questionKey] === oIndex;
                              
                              return (
                                <label
                                  key={oIndex}
                                  className={`flex items-center p-5 rounded-lg border-2 cursor-pointer transition-colors ${
                                    isSelected
                                      ? 'border-[#E8934D] bg-[#E8934D]/10 border-4'
                                      : 'border-[#D4A574] hover:bg-[#F4E4C1]'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name={`question-${currentQuestionIndex}`}
                                    checked={isSelected}
                                    onChange={() => handleQuizAnswer(questionKey, oIndex)}
                                    className="mr-4 w-5 h-5"
                                  />
                                  <span className="text-lg text-[#3D2817] font-medium">
                                    {String.fromCharCode(65 + oIndex)}. {option}
                                  </span>
                                </label>
                              );
                            })}
                          </div>

                          {/* Navigation Buttons */}
                          <div className="flex flex-col sm:flex-row gap-4 mt-8">
                            {currentQuestionIndex > 0 && (
                              <button
                                onClick={() => setCurrentQuestionIndex(currentQuestionIndex - 1)}
                                className="retro-button-small flex-1"
                              >
                                ← Previous
                              </button>
                            )}
                            {currentQuestionIndex < currentQuiz.questions.length - 1 && (
                              <button
                                onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
                                className="retro-button-small flex-1"
                              >
                                Next →
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Submit Button */}
                      <div className="sticky bottom-4">
                        <button
                          onClick={submitQuiz}
                          className="w-full retro-button text-xl py-4"
                          data-testid="submit-quiz"
                        >
                          Submit Test
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Quiz Results and Detailed Review */
                    <div>
                      {/* Summary Section */}
                      <div className="text-center mb-8">
                        <div className={`p-8 rounded-lg ${quizResult.passed ? 'bg-[#4D9E7C]/20' : 'bg-[#E85D4D]/20'} mb-6`}>
                          <div className="text-6xl mb-4">{quizResult.passed ? '🎉' : '📚'}</div>
                          <h3 className="text-3xl retro-title mb-2">
                            {quizResult.passed ? 'Congratulations!' : 'Keep Learning!'}
                          </h3>
                          <p className="text-2xl text-[#3D2817] mb-4">
                            Your Score: {quizResult.score.toFixed(1)}%
                          </p>
                          <p className="text-lg text-[#6B4423]">
                            {quizResult.correct_count} out of {quizResult.total_questions} correct
                          </p>
                        </div>
                      </div>

                      {/* Detailed Question Review */}
                      {quizResult.review_questions && quizResult.review_questions.length > 0 && (
                        <div className="mb-8">
                          <h4 className="text-2xl retro-title text-[#2C1810] mb-6 text-center">Answer Review</h4>
                          <div className="space-y-6">
                            {quizResult.review_questions.map((reviewQ, index) => (
                              <div key={index} className="cassette-card p-6">
                                {/* Question Header */}
                                <div className="flex items-start gap-3 mb-4">
                                  <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                                    reviewQ.is_correct ? 'bg-[#4D9E7C]' : 'bg-[#E85D4D]'
                                  }`}>
                                    {reviewQ.is_correct ? '✓' : '✗'}
                                  </span>
                                  <div className="flex-1">
                                    <p className="text-lg font-semibold text-[#2C1810] mb-3">
                                      Question {index + 1}: {reviewQ.question_text}
                                    </p>
                                  </div>
                                </div>

                                {/* Answer Options */}
                                <div className="space-y-2 mb-4">
                                  {reviewQ.options.map((option, optionIndex) => {
                                    const isUserAnswer = reviewQ.user_answer_index === optionIndex;
                                    const isCorrectAnswer = reviewQ.correct_answer_index === optionIndex;
                                    
                                    let bgColor = 'bg-white';
                                    let borderColor = 'border-[#E8C49A]';
                                    let textColor = 'text-[#6B4423]';
                                    
                                    if (isCorrectAnswer) {
                                      bgColor = 'bg-[#4D9E7C]/10';
                                      borderColor = 'border-[#4D9E7C]';
                                      textColor = 'text-[#2C1810]';
                                    }
                                    
                                    if (isUserAnswer && !isCorrectAnswer) {
                                      bgColor = 'bg-[#E85D4D]/10';
                                      borderColor = 'border-[#E85D4D]';
                                    }
                                    
                                    return (
                                      <div 
                                        key={optionIndex}
                                        className={`p-3 rounded-lg border-2 ${bgColor} ${borderColor}`}
                                      >
                                        <div className="flex items-center gap-3">
                                          <span className={`font-bold ${textColor}`}>
                                            {String.fromCharCode(65 + optionIndex)}.
                                          </span>
                                          <span className={`flex-1 ${textColor}`}>{option}</span>
                                          {isCorrectAnswer && (
                                            <span className="text-[#4D9E7C] font-bold text-sm">
                                              ✓ Correct Answer
                                            </span>
                                          )}
                                          {isUserAnswer && !isCorrectAnswer && (
                                            <span className="text-[#E85D4D] font-bold text-sm">
                                              Your Answer
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Explanation */}
                                {reviewQ.explanation && (
                                  <div className="bg-[#F4E4C1] p-4 rounded-lg">
                                    <p className="text-sm font-semibold text-[#6B4423] mb-2">Explanation:</p>
                                    <p className="text-[#3D2817]">{reviewQ.explanation}</p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <button
                          onClick={() => {
                            setQuizResult(null);
                            setQuizAnswers({});
                            setCurrentQuestionIndex(0);
                          }}
                          className="retro-button"
                        >
                          <RotateCcw className="w-4 h-4 inline mr-2" />
                          Retake Test
                        </button>
                        <button
                          onClick={() => {
                            setCurrentQuiz(null);
                            setQuizResult(null);
                            setQuizAnswers({});
                            loadCourse(); // Reload to update progress
                          }}
                          className="retro-button-outline"
                        >
                          Continue Course
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Mobile Mark Complete Button - shown at bottom */}
                  {currentContent && !isContentCompleted(currentContent.id) && (
                    <div className="md:hidden mt-6 pt-6 border-t-2 border-[#E8C49A]">
                      <button
                        onClick={() => {
                          const chapter = course.chapters.find(ch => 
                            ch.contents.some(c => c.id === currentContent.id)
                          );
                          if (chapter) {
                            markComplete(chapter.id, currentContent.id);
                          }
                        }}
                        className="retro-button w-full"
                      >
                        ✓ Mark Complete
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="admin-card text-center">
                  <p className="text-[#6B4423] text-xl">Select a lesson or quiz to begin</p>
                </div>
              )}
            </div>

          </div>
        </div>
      </section>
    </Layout>
  );
}
